import {
  AR_MOBILE,
  buildFfmpegCommand,
  estimateOutputSize,
  evaluateAgainstProfile,
  getProfileById,
  probe,
} from 'trace-viewer/media-utils';
import type {
  ProfileId,
  TranscodeProfile,
  Verdict,
  VideoProbeReport,
} from 'trace-viewer/media-utils';
import { copyWithFeedback, downloadBlob, resetDisclosures, wireDisclosures, wireFileInput } from '../shared/ui';
import { qualityOf, renderApp } from './render';
import { videoPlayer } from './player';

export interface TranscodeSupport {
  supported: boolean;
  reason?: string;
}

export interface UiState {
  file: File | null;
  /** Quick container scan, available before the full probe finishes. */
  sniffReport: VideoProbeReport | null;
  report: VideoProbeReport | null;
  selectedProfile: TranscodeProfile;
  phase: 'idle' | 'sniffing' | 'probing' | 'done' | 'error';
  error: string | null;
  support: TranscodeSupport | null;
  optimize: {
    running: boolean;
    ratio: number;
    stage: string;
    output: Blob | null;
    outputName: string | null;
    /** Profile the output was made with — a quality change makes it stale. */
    outputProfileId: ProfileId | null;
    /** Probe of the output, so the checks describe what will be downloaded. */
    outputReport: VideoProbeReport | null;
    error: string | null;
  };
}

const idleOptimize: UiState['optimize'] = {
  running: false, ratio: 0, stage: '', output: null, outputName: null, outputProfileId: null, outputReport: null, error: null,
};

const initialState: UiState = {
  file: null,
  sniffReport: null,
  report: null,
  selectedProfile: AR_MOBILE,
  phase: 'idle',
  error: null,
  support: null,
  optimize: idleOptimize,
};

let state: UiState = { ...initialState };

const root = document.getElementById('app')!;

function setState(patch: Partial<UiState>): void {
  state = { ...state, ...patch };
  render();
}

function setOptimize(patch: Partial<UiState['optimize']>): void {
  state = { ...state, optimize: { ...state.optimize, ...patch } };
  render();
}

/** True when the verdict describes the optimized file rather than the original. */
function judgingOutput(): boolean {
  const o = state.optimize;
  return o.outputReport != null && o.outputProfileId === state.selectedProfile.id;
}

function currentVerdict(): Verdict | null {
  const report = judgingOutput() ? state.optimize.outputReport : state.report;
  if (!report) return null;
  return evaluateAgainstProfile(report, state.selectedProfile);
}

function currentCommand(): string {
  if (!state.report) return '';
  return buildFfmpegCommand(state.report, state.selectedProfile, state.file?.name ?? 'input.mp4', 'output.mp4');
}

function render(): void {
  renderApp(root, {
    state,
    verdict: currentVerdict(),
    judgingOutput: judgingOutput(),
    command: currentCommand(),
    estimate: state.report ? estimateOutputSize(state.report, state.selectedProfile) : null,
  });
}

async function handleFile(file: File): Promise<void> {
  resetDisclosures();
  setState({
    file,
    sniffReport: null,
    report: null,
    phase: 'sniffing',
    error: null,
    optimize: idleOptimize,
  });
  const slot = document.getElementById('player-slot');
  if (slot) videoPlayer.show(slot);
  videoPlayer.setOriginal(file);
  try {
    const report = await probe(file, {
      onSniffComplete: (sniffReport) => {
        setState({ sniffReport, phase: 'probing' });
      },
    });
    setState({ report, phase: 'done' });
  } catch (err) {
    videoPlayer.hide();
    setState({ phase: 'error', error: (err as Error).message ?? 'Could not read this video' });
  }
}

async function handleOptimize(): Promise<void> {
  if (!state.file || !state.report || state.optimize.running) return;
  const profile = state.selectedProfile;
  setOptimize({ running: true, ratio: 0, stage: 'loading', output: null, outputProfileId: null, outputReport: null, error: null });
  try {
    const mod = await import('trace-viewer/media-utils/transcode');
    if (!state.support) {
      const support = await mod.isTranscodeSupported();
      setState({ support });
    }
    const blob = await mod.transcode(state.file, profile, state.report, (p) => {
      setOptimize({ ratio: p.ratio, stage: p.stage });
    });
    const base = state.file.name.replace(/\.[^.]+$/, '');
    const outName = `${base}-ar-${qualityOf(profile.id).label.toLowerCase()}.mp4`;
    // Re-check the result so the status and fix list describe the file the
    // user will download. A failed re-check only loses that — keep the output.
    let outputReport: VideoProbeReport | null = null;
    try {
      outputReport = await probe(new File([blob], outName, { type: 'video/mp4' }));
    } catch (err) {
      console.warn('Could not re-check the optimized video:', err);
    }
    setOptimize({ running: false, ratio: 1, stage: 'done', output: blob, outputName: outName, outputProfileId: profile.id, outputReport });
    videoPlayer.setOptimized(blob);
  } catch (err) {
    setOptimize({ running: false, error: (err as Error).message ?? 'Optimizing failed' });
  }
}

root.addEventListener('click', (e) => {
  const target = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
  if (!target) return;
  const action = target.dataset.action;

  if (action === 'optimize') {
    void handleOptimize();
  } else if (action === 'download-output' && state.optimize.output && state.optimize.outputName) {
    downloadBlob(state.optimize.output, state.optimize.outputName);
  } else if (action === 'copy-command') {
    copyWithFeedback(target as HTMLButtonElement, currentCommand());
  }
});

root.addEventListener('change', (e) => {
  const target = e.target as HTMLElement;
  if (target.matches('input[name="quality"]')) {
    const id = (target as HTMLInputElement).value as ProfileId;
    setState({ selectedProfile: getProfileById(id) ?? AR_MOBILE });
  }
});

wireFileInput(root, (file) => void handleFile(file));
wireDisclosures(root);

render();

// Check in-browser optimizing support up front so the page can offer the
// ffmpeg route instead of a button that would fail.
void (async () => {
  try {
    const mod = await import('trace-viewer/media-utils/transcode');
    const support = await mod.isTranscodeSupported();
    setState({ support });
  } catch {
    // Leave support unknown — handleOptimize re-checks and reports errors.
  }
})();
